// src/broadcast/hooks/useMessageBroadcast.ts
"use client"

import { useCallback } from 'react'
import { useBroadcastStore } from '../stores/useBroadcastStore'
import type { MessageRecord } from '../types/events/message'

export interface UseMessageBroadcastOptions {
  conversationId?: string
}

export interface UseMessageBroadcastReturn {
  /** All messages for the conversation (sorted by created_at) */
  messages: MessageRecord[]

  /** Pending (optimistic) messages */
  pendingMessages: MessageRecord[]

  /** Add optimistic message before server confirmation */
  addOptimisticMessage: (tempId: string, message: MessageRecord) => void

  /** Confirm optimistic message with server response */
  confirmMessage: (tempId: string, message: MessageRecord) => void

  /** Remove failed optimistic message */
  removeOptimisticMessage: (tempId: string) => void

  /** Get a specific message by ID */
  getMessage: (messageId: string) => MessageRecord | undefined
}

/**
 * Hook for message-specific broadcast operations
 * Provides filtered access to messages and optimistic update helpers
 */
export function useMessageBroadcast({
  conversationId,
}: UseMessageBroadcastOptions = {}): UseMessageBroadcastReturn {
  // Get store state and actions
  const getMessagesByConversation = useBroadcastStore((s) => s.getMessagesByConversation)
  const pendingMessagesMap = useBroadcastStore((s) => s.pendingMessages)
  const addPendingMessage = useBroadcastStore((s) => s.addPendingMessage)
  const confirmPendingMessage = useBroadcastStore((s) => s.confirmPendingMessage)
  const removePendingMessage = useBroadcastStore((s) => s.removePendingMessage)
  const getMessageFromStore = useBroadcastStore((s) => s.getMessage)

  // Get messages for conversation
  const messages = conversationId
    ? getMessagesByConversation(conversationId)
    : []

  // Get pending messages (filter by conversation if provided)
  const pendingMessages = Array.from(pendingMessagesMap.values())
    .filter((msg) => !conversationId || msg.conversation_id === conversationId)

  const addOptimisticMessage = useCallback((tempId: string, message: MessageRecord) => {
    addPendingMessage(tempId, message)
  }, [addPendingMessage])

  const confirmMessage = useCallback((tempId: string, message: MessageRecord) => {
    confirmPendingMessage(tempId, message)
  }, [confirmPendingMessage])

  const removeOptimisticMessage = useCallback((tempId: string) => {
    removePendingMessage(tempId)
  }, [removePendingMessage])

  const getMessage = useCallback((messageId: string) => {
    return getMessageFromStore(messageId)
  }, [getMessageFromStore])

  return {
    messages,
    pendingMessages,
    addOptimisticMessage,
    confirmMessage,
    removeOptimisticMessage,
    getMessage,
  }
}
